<app-page-wrapper [title]="'sourceExplorer' | i18n">
  <div class="tw-flex tw-flex-col tw-w-full">
    <ng-container *appRoleScope="'BETA_ACCESS'">
      <div
        class="tw-flex tw-flex-col tw-w-full tw-pb-6 tw-mb-6 tw-border-0 tw-border-b tw-border-dt-border-secondary"
      >
        <app-auto-redemption-test-panel />
      </div>
    </ng-container>

    <!-- ── Vault Explorer ── -->
    <div
      class="tw-flex tw-flex-col tw-gap-4 tw-w-full tw-pb-6 tw-border-0 tw-border-b tw-border-dt-border-secondary tw-mb-6"
    >
      <h3 class="tw-text-lg tw-font-semibold tw-text-dt-text-headings tw-m-0">
        Vault Items Explorer
      </h3>

      @if (vaultLoading()) {
        <p class="tw-text-dt-text-secondary tw-text-sm">Loading vault data…</p>
      } @else {
        <div class="tw-flex tw-gap-6 tw-w-full tw-min-h-[20rem]">
          <!-- Left: Type selector -->
          <div class="tw-flex tw-flex-col tw-gap-1 tw-w-64 tw-shrink-0">
            <span class="tw-font-medium tw-text-dt-text-secondary tw-text-sm tw-mb-1"
              >Vault Type</span
            >
            <div
              appGlossScroll
              class="tw-flex tw-flex-col tw-border tw-border-dt-border-secondary tw-rounded-lg tw-overflow-y-auto tw-max-h-[28rem] tw-bg-dt-surface-elevated"
            >
              @for (code of vaultTypes(); track code) {
                <button
                  type="button"
                  class="tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-0 tw-cursor-pointer hover:tw-bg-dt-surface-sunken tw-transition-colors"
                  [class.tw-bg-teal-50]="selectedVaultType() === code"
                  [class.tw-font-semibold]="selectedVaultType() === code"
                  [class.tw-text-teal-800]="selectedVaultType() === code"
                  (click)="onVaultTypeSelected(vaultExplorer.typeLabel(code))"
                >
                  {{ vaultExplorer.typeLabel(code) }}
                  @if (vaultTypeCounts()?.get(code); as count) {
                    <span class="tw-text-dt-text-disabled tw-font-normal tw-ml-1"
                      >({{ count }})</span
                    >
                  }
                </button>
              }
            </div>
          </div>

          <!-- Right: Items list — delegated to per-type components -->
          <div class="tw-flex tw-flex-col tw-gap-3 tw-flex-1 tw-min-w-0">
            @if (!selectedVaultType()) {
              <p class="tw-text-dt-text-tertiary tw-text-sm tw-m-0">
                Select a vault type to view items.
              </p>
            } @else if (vaultLoadingType()) {
              <p class="tw-text-dt-text-secondary tw-text-sm tw-m-0">
                Loading data for selected type…
              </p>
            } @else {
              @switch (selectedVaultType()) {
                @case (VaultCodeEnum.Transaction) {
                  <app-vault-type-transaction [vaultType]="selectedVaultType()!" />
                }
                @case (VaultCodeEnum.SourceTransaction) {
                  <app-vault-type-source-transaction [vaultType]="selectedVaultType()!" />
                }
                @case (VaultCodeEnum.Book) {
                  <app-vault-type-account [vaultType]="selectedVaultType()!" />
                }
                @case (VaultCodeEnum.SourceBook) {
                  <app-vault-type-account [vaultType]="selectedVaultType()!" />
                }
                @default {
                  <app-vault-type-generic [vaultType]="selectedVaultType()!" />
                }
              }
            }
          </div>
        </div>
      }
    </div>

    <!-- ── Source Explorer (existing) ── -->
    <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full tw-pb-6">
      <div class="tw-flex tw-gap-4 tw-w-full">
        <div
          class="tw-flex tw-flex-col tw-gap-7 tw-w-full tw-max-w-3xl tw-border-0 tw-border-b tw-border-dt-border-secondary tw-pb-8"
        >
          <app-dropdown-select
            class="tw-w-full"
            [label]="'sourceExplorerSelectAccount' | i18n"
            [placeholder]="'sourceExplorerSelectAccountPlaceholder' | i18n"
            [options]="accountDropdownOptions()"
            [selectedOption]="selectedAccountName()"
            (optionSelected)="onAccountDropdownSelected($event)"
          >
          </app-dropdown-select>

          <app-dropdown-select
            class="tw-w-full"
            [label]="'sourceExplorerBalances' | i18n"
            [placeholder]="'sourceExplorerBalancesPlaceholder' | i18n"
            [options]="balanceDropdownOptions()"
            [selectedOption]="selectedBalanceLabel()"
            [isEnabled]="balanceSelectEnabled()"
            (optionSelected)="onBalanceDropdownSelected($event)"
          >
          </app-dropdown-select>

          <div class="tw-flex tw-flex-col tw-w-full">
            <app-input
              class="tw-w-full"
              [label]="'sourceExplorerDescriptionFilter' | i18n"
              [placeholder]="'sourceExplorerDescriptionFilterPlaceholder' | i18n"
              [value]="descriptionFilter()"
              [isRequired]="false"
              (outputValue)="onDescriptionOutput($event)"
            >
            </app-input>
            <span class="tw-text-sm tw-text-dt-text-secondary tw-block tw-mt-2">
              {{ "sourceExplorerDescriptionHint" | i18n }}
            </span>
          </div>

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
            <span class="tw-font-medium tw-text-dt-text-headings">{{
              "sourceExplorerDateRange" | i18n
            }}</span>
            <span class="tw-text-sm tw-text-dt-text-secondary">{{
              "sourceExplorerDateHint" | i18n
            }}</span>
            @for (n of [datePickerNonce()]; track n) {
              <div
                class="tw-flex tw-items-center tw-justify-between tw-gap-4 sm:tw-gap-2 tw-w-full"
              >
                <app-date-picker
                  class="tw-w-full"
                  [dateText]="dateFilterStartDisplay()"
                  [restrictDirection]="'right'"
                  [selectedDateRange]="pickerDateRange()"
                  [isRequired]="false"
                  [hasError]="false"
                  [borderType]="'left'"
                  [dateInputLabel]="'sourceExplorerFrom' | i18n"
                  (changeDateEvent)="onDateFilterChange($event, 'start')"
                >
                </app-date-picker>
                <app-date-picker
                  class="tw-w-full"
                  [dateText]="dateFilterEndDisplay()"
                  [restrictDirection]="'left'"
                  [selectedDateRange]="pickerDateRange()"
                  [isRequired]="false"
                  [hasError]="false"
                  [borderType]="'right'"
                  [dateInputLabel]="'sourceExplorerTo' | i18n"
                  (changeDateEvent)="onDateFilterChange($event, 'end')"
                >
                </app-date-picker>
              </div>
            }
          </div>

          <div class="tw-flex tw-flex-wrap tw-justify-end tw-items-center tw-gap-3 tw-pt-1">
            <button mat-stroked-button type="button" (click)="clearDescriptionFilter()">
              {{ "sourceExplorerClearDescription" | i18n }}
            </button>
            <button mat-stroked-button type="button" (click)="clearDateFilter()">
              {{ "sourceExplorerClearDates" | i18n }}
            </button>
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-w-full">
          <!-- display the selected balances raw data here as JSON  -->
          <pre
            appGlossScroll
            tabindex="0"
            class="tw-m-0 tw-text-xs tw-font-mono tw-h-[min(32rem,70vh)] tw-overflow-auto tw-p-3 tw-bg-black gs-dark tw-text-white tw-rounded tw-whitespace-pre-wrap tw-break-words tw-border tw-border-neutral-700/50"
          >
          {{ selectedBalancesRawJson() }}
        </pre
          >
        </div>
      </div>

      @if (!selectedAccountId()) {
        <p class="tw-text-dt-text-secondary tw-text-sm">{{ "sourceExplorerHint" | i18n }}</p>
      } @else if (filteredSourceTransactions().length === 0) {
        <p class="tw-text-dt-text-secondary tw-text-sm">
          {{ "sourceExplorerNoTransactions" | i18n }}
        </p>
      } @else {
        <div
          class="tw-flex tw-flex-wrap tw-gap-6 tw-text-sm tw-border tw-border-dt-border-secondary tw-rounded-lg tw-px-4 tw-py-3 tw-bg-dt-surface-elevated/80"
        >
          <div>
            <span class="tw-text-dt-text-secondary"
              >{{ "sourceExplorerTotalsRowCount" | i18n }}:</span
            >
            <span class="tw-ml-1 tw-font-medium">{{ totals().count }}</span>
          </div>
          <div>
            <span class="tw-text-dt-text-secondary">{{ "sourceExplorerTotalsNet" | i18n }}:</span>
            <span class="tw-ml-1 tw-font-medium">{{ totals().net | number: "1.2-2" }}</span>
            <span class="tw-ml-1 tw-text-dt-text-tertiary">{{ selectedAccount()?.currency }}</span>
          </div>
          <div>
            <span class="tw-text-dt-text-secondary">{{ "sourceExplorerTotalsIn" | i18n }}:</span>
            <span class="tw-ml-1 tw-font-medium">{{ totals().totalIn | number: "1.2-2" }}</span>
          </div>
          <div>
            <span class="tw-text-dt-text-secondary">{{ "sourceExplorerTotalsOut" | i18n }}:</span>
            <span class="tw-ml-1 tw-font-medium">{{ totals().totalOut | number: "1.2-2" }}</span>
          </div>
        </div>

        <div
          appGlossScroll
          class="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-dt-border-secondary"
        >
          <table class="tw-w-full tw-border-collapse tw-text-left tw-text-sm">
            <thead class="tw-bg-dt-surface-elevated tw-text-dt-text-secondary">
              <tr>
                <th class="tw-px-3 tw-py-2 tw-font-medium">{{ "sourceExplorerColDate" | i18n }}</th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColDescription" | i18n }}
                </th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">{{ "sourceExplorerColKind" | i18n }}</th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColDirection" | i18n }}
                </th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColQuantity" | i18n }}
                </th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColCurrency" | i18n }}
                </th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColCategories" | i18n }}
                </th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">{{ "sourceExplorerColView" | i18n }}</th>
                <th class="tw-px-3 tw-py-2 tw-font-medium">
                  {{ "sourceExplorerColLinkedTx" | i18n }}
                </th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredSourceTransactions(); track row.id) {
                <tr
                  class="tw-border-0 tw-border-t tw-border-color-border-subtle hover:tw-bg-dt-surface-elevated/80"
                >
                  <td class="tw-px-3 tw-py-2 tw-whitespace-nowrap">{{ row.transactionDate }}</td>
                  <td class="tw-px-3 tw-py-2">{{ row.description }}</td>
                  <td class="tw-px-3 tw-py-2">{{ row.kind }}</td>
                  <td class="tw-px-3 tw-py-2">{{ directionLabel(row.direction) }}</td>
                  <td class="tw-px-3 tw-py-2 tw-font-mono">{{ row.quantity }}</td>
                  <td class="tw-px-3 tw-py-2">{{ row.currency }}</td>
                  <td class="tw-px-3 tw-py-2">{{ categoriesLabel(row) }}</td>
                  <td class="tw-px-3 tw-py-2">
                    @if (hasLinkedTransaction(row)) {
                      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <button
                          type="button"
                          class="tw-text-dt-text-action tw-underline hover:tw-text-teal-900 tw-font-medium tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
                          (click)="toggleVaultTransactionDetail(row)"
                        >
                          {{ "sourceExplorerViewLinked" | i18n }}
                        </button>
                        <button
                          type="button"
                          class="tw-text-dt-text-secondary tw-underline hover:tw-text-dt-text-headings tw-font-medium tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
                          (click)="toggleJsonDetail(row)"
                        >
                          {{ "sourceExplorerViewJson" | i18n }}
                        </button>
                      </div>
                    } @else {
                      <span class="tw-text-red-600 tw-font-bold">X</span>
                    }
                  </td>
                  <td class="tw-px-3 tw-py-2 tw-font-mono tw-text-xs">{{ row.sourceId }}</td>
                </tr>
                @if (linkedTxForExpandedRow(row); as vtx) {
                  <tr class="tw-bg-dt-surface-elevated/80">
                    <td
                      [attr.colspan]="tableColCount"
                      class="tw-px-3 tw-py-4 tw-border-0 tw-border-t tw-border-dt-border-secondary"
                    >
                      <div
                        class="tw-border-0 tw-border-l-4 tw-border-teal-600 tw-pl-4 tw-space-y-1"
                      >
                        <div class="tw-font-medium tw-text-dt-text-headings tw-mb-2">
                          {{ "sourceExplorerVaultTxTitle" | i18n }}
                        </div>
                        <div appGlossScroll class="tw-overflow-x-auto">
                          <table
                            class="tw-w-full tw-text-left tw-text-xs tw-border tw-border-dt-border-secondary tw-rounded"
                          >
                            <thead>
                              <tr class="tw-bg-dt-surface-elevated">
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColDate" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColDescription" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColKind" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColDirection" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColQuantity" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColCurrency" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerColCategories" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerTxDefinition" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerTxOrigin" | i18n }}
                                </th>
                                <th class="tw-px-2 tw-py-1 tw-font-medium">
                                  {{ "sourceExplorerTxStatus" | i18n }}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td class="tw-px-2 tw-py-1 tw-whitespace-nowrap">
                                  {{ formatTxPostDate(vtx) }}
                                </td>
                                <td class="tw-px-2 tw-py-1">{{ vtx.description }}</td>
                                <td class="tw-px-2 tw-py-1">{{ vtx.kind }}</td>
                                <td class="tw-px-2 tw-py-1">{{ directionLabel(vtx.direction) }}</td>
                                <td class="tw-px-2 tw-py-1 tw-font-mono">
                                  {{ txQuantityAmount(vtx) }}
                                </td>
                                <td class="tw-px-2 tw-py-1">{{ txCurrency(vtx) }}</td>
                                <td class="tw-px-2 tw-py-1">{{ txCategoriesLabel(vtx) }}</td>
                                <td class="tw-px-2 tw-py-1">{{ vtx.definition }}</td>
                                <td class="tw-px-2 tw-py-1">{{ originLabel(vtx.origin) }}</td>
                                <td class="tw-px-2 tw-py-1">{{ vtx.status }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
                @if (isJsonExpanded(row.id)) {
                  <tr class="tw-bg-dt-surface-sunken/80">
                    <td
                      [attr.colspan]="tableColCount"
                      class="tw-w-full tw-min-w-0 tw-max-w-none tw-px-3 tw-py-4 tw-border-0 tw-border-t tw-border-dt-border-secondary"
                    >
                      <div
                        class="tw-border-0 tw-border-l-4 tw-border-neutral-500 tw-pl-4 tw-space-y-5 tw-w-full tw-min-w-0 tw-max-w-full"
                      >
                        <!-- 1. Categories JSON (full width, expanded by default; toggle to collapse) -->
                        @if (findLinkedTransaction(row.sourceId); as jsonTxCategories) {
                          <div class="tw-space-y-2">
                            <div
                              class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-gap-y-2"
                            >
                              <div class="tw-text-sm tw-font-medium tw-text-dt-text-headings">
                                {{ "sourceExplorerJsonCategoriesHeading" | i18n }}
                              </div>
                              <button
                                mat-stroked-button
                                type="button"
                                class="tw-shrink-0"
                                (click)="toggleJsonPanelCategories()"
                              >
                                @if (jsonPanelShowCategories()) {
                                  {{ "sourceExplorerJsonHideCategories" | i18n }}
                                } @else {
                                  {{ "sourceExplorerJsonShowCategories" | i18n }}
                                }
                              </button>
                            </div>
                            @if (jsonPanelShowCategories()) {
                              <pre
                                appGlossScroll
                                tabindex="0"
                                class="tw-m-0 tw-w-full tw-text-xs tw-font-mono tw-max-h-[min(18rem,45vh)] tw-overflow-auto tw-p-3 tw-bg-black gs-dark tw-text-white tw-rounded tw-whitespace-pre-wrap tw-break-words tw-border tw-border-neutral-700/50"
                                >{{ categoriesJsonForTransaction(jsonTxCategories) }}</pre
                              >
                            }
                          </div>
                        }

                        <!-- 2. TransactionView JSON | source raw (two equal columns) -->
                        <div class="tw-space-y-2">
                          <div class="tw-text-sm tw-font-medium tw-text-dt-text-headings">
                            {{ "sourceExplorerJsonTxAndSourceSection" | i18n }}
                          </div>
                          <div
                            class="tw-flex tw-w-full tw-min-w-0 tw-gap-4 md:tw-flex-row md:tw-items-stretch md:tw-gap-6"
                          >
                            <div
                              class="tw-flex tw-min-w-0 tw-basis-0 tw-flex-1 tw-flex-col tw-gap-2"
                            >
                              <div class="tw-text-xs tw-font-medium tw-text-dt-text-secondary">
                                {{ "sourceExplorerJsonVaultTitle" | i18n }}
                              </div>
                              @if (findLinkedTransaction(row.sourceId); as jsonTx) {
                                <pre
                                  appGlossScroll
                                  tabindex="0"
                                  class="tw-m-0 tw-text-xs tw-font-mono tw-h-[min(32rem,70vh)] tw-overflow-auto tw-p-3 tw-bg-black gs-dark tw-text-white tw-rounded tw-whitespace-pre-wrap tw-break-words tw-border tw-border-neutral-700/50"
                                  >{{ transactionViewAsJson(jsonTx) }}</pre
                                >
                              } @else {
                                <p class="tw-text-dt-text-secondary tw-text-sm tw-m-0">
                                  {{ "sourceExplorerVaultTxJsonMissing" | i18n }}
                                </p>
                              }
                            </div>
                            <div
                              class="tw-flex tw-min-w-0 tw-basis-0 tw-flex-1 tw-flex-col tw-gap-2"
                            >
                              <div class="tw-text-xs tw-font-medium tw-text-dt-text-secondary">
                                {{ "sourceExplorerJsonSourceTitle" | i18n }}
                              </div>
                              <pre
                                appGlossScroll
                                tabindex="0"
                                class="tw-m-0 tw-text-xs tw-font-mono tw-h-[min(32rem,70vh)] tw-overflow-auto tw-p-3 tw-bg-black gs-dark tw-text-white tw-rounded tw-whitespace-pre-wrap tw-break-words tw-border tw-border-neutral-700/50"
                                >{{ sourceRawJsonPretty(row) }}</pre
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</app-page-wrapper>
